import { forwardRef } from "react";
import type { PopoverStateReturn } from "reakit";
import { VisuallyHidden } from "reakit";
import { CrossCircle, InformationCircle, QuestionCircle } from "@mos-cat/ds-icons";
import { pxToRem } from "@mos-cat/ds-theme";
import { Box } from "../Box/Box";
import { PopoverDisclosure } from "./Popover";
import { useRequiredPropWarning } from "../hooks/useRequiredPropWarning";
import { WHCM } from "../constants";
import { UtilityButton } from "../UtilityButton/UtilityButton";

/**
 * @public
 */
export type InfoPopoverDisclosureProps = {
  /**
   * Иконка в кнопке.
   */
  icon?: "info" | "question";
  /**
   * Подпись кнопки для скринридера
   */
  label: string;
  /**
   * Объект возвращаемый из хука `usePopoverState`
   */
  popoverState: PopoverStateReturn;
};

/**
 * @public
 * Специальный вариант дискложера для поповера.
 */
export const InfoPopoverDisclosure = forwardRef<HTMLButtonElement, InfoPopoverDisclosureProps>(
  function InfoPopoverDisclosure({ icon = "info", popoverState, label }, ref) {
    useRequiredPropWarning(Boolean(label), "InfoPopoverDisclosure", "label");

    const Icon = {
      info: InformationCircle,
      question: QuestionCircle,
    };

    return (
      <PopoverDisclosure
        ref={ref}
        as={UtilityButton}
        {...popoverState}
        styles={{
          boxSizing: "border-box",
          height: pxToRem(32),
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          borderRadius: "button",
          lineHeight: "btn32LineHeight",
          border: 1,
          fontSize: "btn32FontSize",
          borderColor: "transparent",
          ".mc0-popoverIcon": {
            color: "popoverInfoColor",
          },

          "&:hover .mc0-popoverIcon": {
            color: "popoverInfoHoverColor",
          },

          "&:active .mc0-popoverIcon": {
            color: "popoverInfoActiveColor",
          },
          [WHCM]: {
            '[class*="symbol"]': {
              fill: "Highlight",
            },
          },
        }}
      >
        <VisuallyHidden>{label}</VisuallyHidden>
        <Box as="span" display="flex" alignItems="center">
          <Box as="span" className="mc0-popoverIcon" aria-hidden>
            {popoverState.visible ? (
              <Box size="1.428em" as={CrossCircle} />
            ) : (
              <Box size="1.428em" as={Icon[icon]} />
            )}
          </Box>
        </Box>
      </PopoverDisclosure>
    );
  }
);
